import { gsap, ScrollTrigger } from '../lib/gsap';
import { prefersReducedMotion } from '../lib/lenis';

// teamix × House of DJ's bundle page (page-house-of-djs.php):
//  - each tap on a buy button reported as the start of a checkout;
//  - the floating buy pill, shown once the hero's price card has scrolled
//    away and hidden whenever a price card is on screen;
//  - numbers that count up the first time they're seen;
//  - a soft light under the cursor on cards (.hod-spot);
//  - the running bands (ribbons, reviews): the one set of items in the HTML
//    repeated to fill twice the width on screen, so moving by half loops
//    with no seam on any screen;
//  - the laptop straightening as it scrolls in, and the syllabus rail
//    filling as you read down it.
// Everything degrades to the static page: no JS, reduced motion, or a failure.

type Tracker = {
  dataLayer?: unknown[];
  fbq?: (...args: unknown[]) => void;
};

export function initHouseOfDjs(): void {
  const root = document.querySelector<HTMLElement>('[data-hod]');
  if (!root) return;

  const reduced = prefersReducedMotion();

  trackBuys(root);
  floatingPill(root);
  activeNav(root);
  if (reduced) return;
  loops(root);
  countUps(root);
  spotlight(root);
  tilt(root);
  rail(root);
}

function trackBuys(root: HTMLElement): void {
  const w = window as unknown as Tracker;
  const price = Number(root.querySelector('.hod-ticket__now')?.textContent?.replace(/[^\d]/g, '')) || undefined;
  root.addEventListener('click', (e) => {
    const a = (e.target as HTMLElement | null)?.closest<HTMLAnchorElement>('[data-hod-buy]');
    // Only a real payment link starts a checkout; the scroll-to-offer fallback doesn't.
    if (!a || a.hasAttribute('data-hod-scroll')) return;
    try {
      w.dataLayer?.push({ event: 'hod_buy_click', hod_where: a.dataset.hodBuy || '' });
      w.fbq?.('track', 'InitiateCheckout', { value: price, currency: 'ILS', content_name: 'teamix x House of DJs' });
    } catch {
      /* tracking must never block the payment link */
    }
  });
}

function floatingPill(root: HTMLElement): void {
  const pill = root.querySelector<HTMLElement>('[data-hod-float]');
  const cards = Array.from(root.querySelectorAll<HTMLElement>('[data-hod-ticket]'));
  const final = root.querySelector<HTMLElement>('.hod-final');
  if (!pill || !cards.length) return;

  let queued = false;
  const onScreen = (el: Element): boolean => {
    const r = el.getBoundingClientRect();
    return r.bottom > 0 && r.top < window.innerHeight;
  };
  const update = (): void => {
    queued = false;
    const pastHero = cards[0].getBoundingClientRect().bottom < 0;
    const covered = cards.some(onScreen) || (final ? onScreen(final) : false);
    pill.classList.toggle('is-shown', pastHero && !covered);
  };
  window.addEventListener('scroll', () => {
    if (!queued) {
      queued = true;
      requestAnimationFrame(update);
    }
  }, { passive: true });
  update();
}

/** "11,700+" → counts 0 → 11,700 keeping its prefix, suffix and separators. */
function countUps(root: HTMLElement): void {
  const els = Array.from(root.querySelectorAll<HTMLElement>('[data-count]'));
  if (!els.length || !('IntersectionObserver' in window)) return;

  const run = (el: HTMLElement): void => {
    const text = el.textContent || '';
    const m = text.match(/^(\D*)([\d,]+)(.*)$/s);
    if (!m) return;
    const [, pre, digits, post] = m;
    const target = Number(digits.replace(/,/g, ''));
    if (!target || target < 10) return;
    const comma = digits.includes(',');
    const state = { v: 0 };
    gsap.to(state, {
      v: target,
      duration: 1.6,
      ease: 'power3.out',
      onUpdate: () => {
        const n = Math.round(state.v);
        el.textContent = pre + (comma ? n.toLocaleString('en-US') : String(n)) + post;
      },
      onComplete: () => { el.textContent = text; },
    });
  };

  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      io.unobserve(entry.target);
      run(entry.target as HTMLElement);
    });
  }, { threshold: 0.6 });
  els.forEach((el) => io.observe(el));
}

function spotlight(root: HTMLElement): void {
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
  root.addEventListener('pointermove', (e) => {
    const card = (e.target as HTMLElement | null)?.closest<HTMLElement>('.hod-spot');
    if (!card) return;
    const r = card.getBoundingClientRect();
    card.style.setProperty('--mx', `${e.clientX - r.left}px`);
    card.style.setProperty('--my', `${e.clientY - r.top}px`);
  });
}

function tilt(root: HTMLElement): void {
  const laptop = root.querySelector<HTMLElement>('[data-hod-tilt]');
  if (!laptop) return;
  gsap.fromTo(
    laptop,
    { rotateX: 22, scale: 0.92, y: 40 },
    {
      rotateX: 0,
      scale: 1,
      y: 0,
      ease: 'none',
      scrollTrigger: { trigger: laptop, start: 'top 95%', end: 'top 35%', scrub: 0.6 },
    }
  );
}

function rail(root: HTMLElement): void {
  const timeline = root.querySelector<HTMLElement>('[data-hod-timeline]');
  const fill = root.querySelector<HTMLElement>('[data-hod-rail]');
  if (!timeline || !fill) return;
  const steps = Array.from(timeline.querySelectorAll<HTMLElement>('[data-hod-step]'));

  gsap.fromTo(fill, { scaleY: 0 }, {
    scaleY: 1,
    ease: 'none',
    scrollTrigger: { trigger: timeline, start: 'top 70%', end: 'bottom 60%', scrub: true },
  });
  steps.forEach((step) => {
    ScrollTrigger.create({
      trigger: step,
      start: 'top 62%',
      onEnter: () => step.classList.add('is-lit'),
      onLeaveBack: () => step.classList.remove('is-lit'),
    });
  });
}

/**
 * Repeat a band's items until one half of the track is at least as wide as
 * its window, then let CSS move the track by exactly half. Rebuilt when the
 * width changes (fonts, rotation, resize).
 */
function loops(root: HTMLElement): void {
  const tracks = Array.from(root.querySelectorAll<HTMLElement>('[data-hod-loop]'));
  if (!tracks.length) return;

  const originals = new Map(tracks.map((t) => [t, Array.from(t.children) as HTMLElement[]]));

  const build = (track: HTMLElement): void => {
    const items = originals.get(track) || [];
    track.classList.remove('is-looping');
    track.replaceChildren(...items);
    const setWidth = track.scrollWidth;
    const view = track.parentElement?.clientWidth || window.innerWidth;
    if (!setWidth) return;
    const perHalf = Math.max(1, Math.ceil(view / setWidth));
    for (let copy = 1; copy < perHalf * 2; copy++) {
      items.forEach((item) => {
        const clone = item.cloneNode(true) as HTMLElement;
        clone.setAttribute('aria-hidden', 'true');
        track.appendChild(clone);
      });
    }
    // Same speed on every screen: duration follows the distance travelled.
    track.style.setProperty('--loop-duration', `${Math.round((setWidth * perHalf) / 60)}s`);
    track.classList.add('is-looping');
  };

  const buildAll = (): void => tracks.forEach(build);
  buildAll();
  document.fonts?.ready.then(buildAll).catch(() => undefined);

  let lastWidth = window.innerWidth;
  let timer = 0;
  window.addEventListener('resize', () => {
    if (window.innerWidth === lastWidth) return; // phones fire resize on scroll (toolbar)
    lastWidth = window.innerWidth;
    window.clearTimeout(timer);
    timer = window.setTimeout(buildAll, 200);
  });
}

/** Light up the bar link of the section on screen, and slide the ink under it. */
function activeNav(root: HTMLElement): void {
  const nav = root.querySelector<HTMLElement>('[data-hod-nav]');
  const ink = nav?.querySelector<HTMLElement>('[data-hod-ink]');
  if (!nav || !ink || !('IntersectionObserver' in window)) return;
  const links = Array.from(nav.querySelectorAll<HTMLAnchorElement>('a[href^="#"]'));
  const byId = new Map(links.map((a) => [a.getAttribute('href')!.slice(1), a]));
  let current: HTMLAnchorElement | null = null;

  const place = (): void => {
    if (!current) {
      ink.style.opacity = '0';
      return;
    }
    ink.style.width = `${current.offsetWidth}px`;
    ink.style.transform = `translateX(${current.offsetLeft}px)`;
    ink.style.opacity = '1';
  };

  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const link = byId.get(entry.target.id) || null;
      if (entry.isIntersecting) current = link;
      // Between tabbed sections (the offer, the closing) the last tab stays lit;
      // only scrolling back up above the first one clears it.
      else if (current === link && link === links[0] && entry.boundingClientRect.top > 0) current = null;
    });
    links.forEach((a) => a.classList.toggle('is-active', a === current));
    place();
  }, { rootMargin: '-45% 0px -50% 0px' });
  byId.forEach((_, id) => {
    const el = document.getElementById(id);
    if (el) io.observe(el);
  });
  window.addEventListener('resize', place, { passive: true });
  document.fonts?.ready.then(place).catch(() => undefined);
}
